// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleObject3DManager } from '../manager/article-object-3d.manager';
import { ArticleRefreshData } from './model/article-refresh-data';
import { ArticleUsageVO, ArticleVO } from '../model/vo/article.vo';
import { ArticleModelDataHolder, EmptyArticleModelData } from '@3d-scene/model3d/cache/model-data';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ArticleUtils } from '../util/article-utils';
import { Dictionary } from 'typescript-collections';
import { ModelLoadingService } from './model-loading.service';
import { ArticleManager } from '../manager/article.manager';
import { ArticleLoadingManager } from './article-loading.manager';
import { Injectable } from '@angular/core';
import { ArticleAccessoryManager } from '../manager/article-accessory.manager';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';

@Injectable({ providedIn: 'root' })
export class ArticleContainerLoader {
  constructor(
    private modelLoadingService: ModelLoadingService,
    private articleManager: ArticleManager,
    private articleObject3DManager: ArticleObject3DManager,
    private articleLoadingManager: ArticleLoadingManager,
    private articleAccessoryManager: ArticleAccessoryManager
  ) {}

  public async refreshContainers(articleContainers: ArticleContainer[], data: ArticleRefreshData): Promise<void> {
    const staticContainers: ArticleContainer[] = [];
    const dynamicContainers: ArticleContainer[] = [];

    articleContainers.forEach((container: ArticleContainer) => {
      // processed only containers need to be parsed/finalized
      if (!container.isParsed) {
        if (data.dynamicArticlesMap && data.dynamicArticlesMap.containsKey(container.articleGUID)) {
          const articleInfo: ArticleInfoVO = ArticleUtils.findArticleInfoByGuidInSource(container.articleGUID, data.dynamicArticlesInfo);
          if (articleInfo && articleInfo.attributes) {
            for (const articleAttribute of articleInfo.attributes) {
              container.properties.setValue(articleAttribute.name, articleAttribute.values);
            }
          }
          if (data.delayDynamicArticleFinalization) {
            this.articleLoadingManager.storePendingArticle(container, articleInfo);
          }
          dynamicContainers.push(container);
        } else {
          staticContainers.push(container);
        }
      }
    });

    await this.loadModels(articleContainers, data);
    await this.refreshStaticContainers(staticContainers, data);
    await this.refreshDynamicContainers(dynamicContainers, data);
    await this.articleLoadingManager.applyTexturesToFinalizedArticles(articleContainers);
  }

  private async loadModels(articleContainers: ArticleContainer[], data: ArticleRefreshData): Promise<void> {
    await this.loadArticleModelsForUsages(articleContainers.map((container: ArticleContainer) => container.articleUsage), data.isArticleRepresentationsEnabled);
  }

  private async loadArticleModelsForUsages(articleUsages: ArticleUsageVO[], isArticleRepresentationsEnabled: boolean): Promise<void> {
    if (!articleUsages) {
      articleUsages = [];
    }
    // collect versions of articles need to be parsed
    const articleIdToVersions: Dictionary<number, number[]> = new Dictionary<number, number[]>();
    articleUsages.forEach((usage: ArticleUsageVO) => {
      if (!articleIdToVersions.containsKey(usage.articleId)) {
        articleIdToVersions.setValue(usage.articleId, []);
      }
      const versions: number[] = articleIdToVersions.getValue(usage.articleId);
      if (versions.indexOf(usage.articleVersion) < 0) {
        versions.push(usage.articleVersion);
      }
    });

    const articleModelDataHolders: ArticleModelDataHolder[] = await this.createArticleModels(articleIdToVersions, isArticleRepresentationsEnabled);
    articleModelDataHolders.forEach((articleModelDataHolder) => this.articleObject3DManager.addArticleObject3DData(articleModelDataHolder));
  }

  private async createArticleModels(
    articleIdToVersions: Dictionary<number, number[]>,
    isArticleRepresentationsEnabled: boolean
  ): Promise<ArticleModelDataHolder[]> {

    const articleModels: ArticleModelDataHolder[] = [];

    for (let identity of articleIdToVersions.keys()) {
      const versions = articleIdToVersions.getValue(identity);
      for (let version of versions) {
        const article: ArticleVO = await this.articleManager.loadFullArticle(identity, version).toPromise();
        this.articleAccessoryManager.loadArticle_success(article);
        if (ArticleUtils.isArticleWithout3DRepresentation(article)) {
          articleModels.push(new ArticleModelDataHolder(article, new EmptyArticleModelData(), 'EmptyArticleModelData', null));
        } else {
          const existingDataHolder: ArticleModelDataHolder = this.articleObject3DManager.findArticleModelDataHolder(identity, version);
          if (!existingDataHolder) {
            articleModels.push(await this.modelLoadingService.loadModel(article, isArticleRepresentationsEnabled));
          } else {
            articleModels.push(existingDataHolder);
          }
        }
      }
    }

    return articleModels;
  }

  private async refreshStaticContainers(staticContainers: ArticleContainer[], data: ArticleRefreshData): Promise<void> {
    const containers = await this.parseContainers(staticContainers, data);
    this.refreshParsedContainers(containers, data);
    containers.forEach(c => c.refreshTextureConnections(data.updateTextures, data.deleteUnusedConnections))
  }

  private async refreshDynamicContainers(dynamicContainers: ArticleContainer[], data: ArticleRefreshData): Promise<void> {
    const originaldNamesMap = this.getConnectionNamesByContainer(dynamicContainers);

    // This multiplies the connections of dynamic articles.
    const containers = await this.parseContainers(dynamicContainers, data);

    // When loading a project, the articles' prototype connections are not removed and remain in the containers' model.
    // This deletes unneeded prototype connections.
    containers.forEach(container => this.cleanupConnectionMeshes(container, originaldNamesMap.get(container)));

    this.refreshParsedContainers(containers, data)
  }

  private cleanupConnectionMeshes(container: ArticleContainer, oldConnectionNames: string[]): void {
    const connectionNames = this.getConnectionNames(container.article);
    const deletedConnectionNames = oldConnectionNames.filter(name => !connectionNames.includes(name));
    // Delete model children named after deleted connections
    container.model.children
      .filter(mesh => deletedConnectionNames.includes(mesh.name))
      .forEach(mesh => Object3DUtils.destroy(mesh));
  }

  private getConnectionNamesByContainer(containers: ArticleContainer[]): Map<ArticleContainer, string[]> {
    return new Map(containers.map(dc => [dc, this.getConnectionNames(dc.article)]));
  }

  private getConnectionNames(article: ArticleVO): string[] {
    return article.metaElements.map(me => me.name);
  }

  private async parseContainers(articleContainers: ArticleContainer[], data: ArticleRefreshData): Promise<ArticleContainer[]> {
    for (let container of articleContainers) {
      const usage: ArticleUsageVO = container.articleUsage;
      if (usage) {
        let modelDataHolder: ArticleModelDataHolder = this.articleObject3DManager.findArticleModelDataHolder(usage.articleId, usage.articleVersion);
        if (modelDataHolder) {
          const dynamicArticle: ArticleVO = data.dynamicArticlesMap ? data.dynamicArticlesMap.getValue(container.articleGUID) : null;
          if (dynamicArticle) {
            modelDataHolder = modelDataHolder.clone() as ArticleModelDataHolder;
            modelDataHolder.article = dynamicArticle;
            await container.parseModel(modelDataHolder);
            if (!this.articleLoadingManager.isPendingArticle(container)) {
              this.articleLoadingManager.dispatchArticleChangeMessage(container, false);
            }
          } else {
            await container.parseModel(modelDataHolder);
          }
        }
      }
    }
    return articleContainers;
  }

  private refreshParsedContainers(articleContainers: ArticleContainer[], data: ArticleRefreshData): void {
    articleContainers.forEach((articleContainer: ArticleContainer) => articleContainer.refreshContainer(data.deleteUnusedConnections));
  }
}
